import React from "react";
import PropTypes from "prop-types";

const CONFIDENCE_STYLE = {
  high:   { dot: "bg-green-500",  text: "text-green-700",  bg: "bg-green-50",  label: "High confidence"   },
  medium: { dot: "bg-yellow-500", text: "text-yellow-700", bg: "bg-yellow-50", label: "Medium confidence" },
  low:    { dot: "bg-red-500",    text: "text-red-700",    bg: "bg-red-50",    label: "Low confidence"    },
};

export default function ServiceCard({ service }) {
  const conf = CONFIDENCE_STYLE[service.confidence] ?? CONFIDENCE_STYLE.medium;

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-5 flex flex-col gap-3">
      {/* Header row */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-slate-900">{service.name}</span>
          {service.kind && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-slate-500 font-medium">
              {service.kind}
            </span>
          )}
        </div>
        <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${conf.bg} ${conf.text}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${conf.dot}`} />
          {conf.label}
        </span>
      </div>

      {/* Description */}
      <p className="text-sm text-slate-600 leading-relaxed">{service.description}</p>

      {/* Evidence references */}
      {service.evidence?.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {service.evidence.slice(0, 2).map((ev) => (
            <span
              key={`${ev.file}:${ev.line}`}
              className="text-xs font-mono px-2 py-0.5 bg-slate-100 text-slate-600 rounded border border-slate-200"
            >
              {ev.file}:{ev.line}
            </span>
          ))}
          {service.evidence.length > 2 && (
            <div className="relative group">
              <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-500 rounded border border-slate-200 cursor-default">
                +{service.evidence.length - 2} more
              </span>
              <div className="absolute left-0 bottom-full mb-1.5 z-50 hidden group-hover:flex">
                <div className="bg-gray-200 text-black text-xs rounded-lg px-3 py-2 shadow-xl flex flex-col gap-1 whitespace-nowrap">
                  {service.evidence.slice(2).map((ev) => (
                    <span key={`${ev.file}:${ev.line}`} className="font-mono">{ev.file}:{ev.line}</span>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      {service.payload?.secret_names?.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {service.payload.secret_names.map((s) => (
            <span key={s} className="text-xs font-mono px-2 py-0.5 bg-slate-100 text-slate-600 rounded border border-slate-200">
              {s}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

ServiceCard.propTypes = {
  service: PropTypes.shape({
    name:        PropTypes.string.isRequired,
    kind:        PropTypes.string,
    confidence:  PropTypes.oneOf(["high", "medium", "low"]).isRequired,
    description: PropTypes.string.isRequired,
    evidence:    PropTypes.arrayOf(PropTypes.shape({
      file:    PropTypes.string.isRequired,
      line:    PropTypes.number.isRequired,
      snippet: PropTypes.string,
    })),
    payload:     PropTypes.object,
  }).isRequired,
};
